import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import moment from 'moment';

import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import { TRANSFER_MARKER } from '../../../utilities/financeInvoice';
import DepositModal from '../../DepositModal';
import EditDepositModal from '../../EditDepositModal';
import TransferFromDealModal from './TransferFromDealModal';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const isTransfer = (deposit = {}) => (deposit.memo || '').includes(TRANSFER_MARKER);

export default function FinanceDepositsPanel({
  deposits = [],
  stockNumber,
  invoiceTotal = 0,
  disabled,
  defaultAccount = '',
  onDepositAdded,
  onDepositDeleted,
  onDepositUpdated,
}) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [transferOpen, setTransferOpen] = React.useState(false);
  const [editDeposit, setEditDeposit] = React.useState(null);
  const isAdmin = StateManager.isAdmin();

  const total = deposits.reduce((s, d) => s + (d.amount || 0), 0);
  const remaining = invoiceTotal - total;
  const progress = invoiceTotal > 0 ? Math.min((total / invoiceTotal) * 100, 100) : 0;
  const paidInFull = invoiceTotal > 0 && remaining <= 0;

  const viewReceipt = async (fileLocation) => {
    const url = await firebase.storage().ref(fileLocation).getDownloadURL();
    window.open(url, '_blank');
  };

  const deleteDeposit = async (id) => {
    if (!window.confirm('Delete this deposit?')) return;
    StateManager.setLoading(true);
    await firebase.firestore().collection('deposits').doc(id).delete();
    onDepositDeleted?.(id);
    StateManager.setLoading(false);
  };

  return (
    <Box>
      {invoiceTotal > 0 && (
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {USD(total)} collected
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: paidInFull ? 'success.dark' : 'warning.dark' }}>
              {paidInFull ? 'Funded in full' : `${USD(remaining)} remaining`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 7, borderRadius: 4, bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: paidInFull ? 'success.main' : 'primary.main' },
            }}
          />
        </Box>
      )}

      {deposits.length > 0 ? (
        <Box sx={{ mx: 2, mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          {deposits.map((dep, i) => (
            <Box
              key={dep.id || i}
              onClick={isAdmin && !disabled ? () => setEditDeposit(dep) : undefined}
              sx={{
                px: 2, py: 1.25,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                borderBottom: i < deposits.length - 1 ? 1 : 0, borderColor: 'divider',
                bgcolor: i % 2 === 1 ? 'grey.50' : 'transparent',
                cursor: isAdmin && !disabled ? 'pointer' : 'default',
                '&:hover': isAdmin && !disabled ? { bgcolor: 'action.hover' } : {},
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{USD(dep.amount)}</Typography>
                  {isTransfer(dep) && (
                    <Chip label="from deal" size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dep.date ? moment(dep.date, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format('MMM D, YYYY') : '—'}
                  {dep.memo ? ` · ${dep.memo}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                {dep.files?.[0] && (
                  <IconButton size="small" title="View attached file" data-sold-ok onClick={(e) => { e.stopPropagation(); viewReceipt(dep.files[0]); }} disabled={disabled}>
                    <ReceiptIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton size="small" color="error" title="Delete deposit" onClick={(e) => { e.stopPropagation(); deleteDeposit(dep.id); }} disabled={disabled}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, pt: invoiceTotal > 0 ? 0 : 2, pb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No finance deposits recorded</Typography>
        </Box>
      )}

      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<SwapHorizIcon />}
          onClick={() => setTransferOpen(true)}
          disabled={disabled || invoiceTotal <= 0}
          title={invoiceTotal <= 0 ? 'Create the finance invoice first' : 'Collect the balance from the deal'}
        >
          Transfer from Deal
        </Button>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={disabled}>
          Add Deposit
        </Button>
      </Box>

      <TransferFromDealModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        stockNumber={stockNumber}
        defaultAmount={remaining > 0 ? remaining : 0}
        defaultAccount={defaultAccount}
        onCreated={(deposit) => onDepositAdded?.(deposit)}
      />

      <DepositModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        stockNumber={stockNumber}
        type="finance"
        defaultAmount={remaining > 0 ? remaining : 0}
        defaultAccount={defaultAccount}
        onSaved={(saved) => onDepositAdded?.(saved)}
      />

      <EditDepositModal
        open={!!editDeposit}
        onClose={() => setEditDeposit(null)}
        deposit={editDeposit}
        onSaved={(updated) => onDepositUpdated?.(updated)}
      />
    </Box>
  );
}
